<template>
  <div class="form-generator">
    <Preloader v-if="isLoading" />
    <FormGenerator :data="form" :isLoading="isLoading" :showReset="false" textButtonSubmit="Save" @submit="onSubmit"
      @updateItem="updateItem">
      <template v-slot:timeOffRules="slotProps">
        <b-form-group>
          <div class="d-flex w-100 justify-content-between pt-3">
            <label class="time-off-rules-label">{{ slotProps.data.label }}</label>
            <b-button type="submit" class="btn btn-secondary add-button"
              @click.prevent="addTimeOffRule(slotProps.data.value)">Add rule</b-button>
          </div>
          <div v-for="(timeOffRule, index) in slotProps.data.value" :key="index" class="my-3 time-off-rules row col-12">
            <b-form-group class="col-4">
              <label class="form__label input-label">Time off length</label>
              <div class="position-relative">
                <b-form-select size="sm" v-model="timeOffRule.limitType" :options="limitTypes"></b-form-select>
                <span class="material-symbols-outlined custom-select-icon">keyboard_arrow_down</span>
              </div>
            </b-form-group>
            <b-form-group class="col-4">
              <label class="form__label input-label">Days</label>
              <b-form-input type="number" class="time-off-rule-component" size="sm" v-model="timeOffRule.limit"
                maxlength="2" />
            </b-form-group>
            <b-form-group class="col-4">
              <label class="form__label input-label">Advance notice (days)</label>
              <b-form-input type="number" class="time-off-rule-component" size="sm" v-model="timeOffRule.buffer"
                maxlength="2" />
            </b-form-group>
            <b-form-group class="col-1 d-flex justify-content-center align-items-end">
              <b-button @click.prevent="removeTimeOffRule(slotProps.data.value, index)" href="#" variant="danger"
                class="delete-btn" title="Delete time off rule"
                v-b-tooltip.html="{ variant: 'base', placement: 'top', placement: 'top', boundary: 'viewport' }">
                <font-awesome-icon icon="trash" />
              </b-button>
            </b-form-group>
          </div>
        </b-form-group>
      </template>
      <template v-slot:color="slotProps">
        <b-form-group>
          <label class="form__label input-label">{{ slotProps.data.label }}</label>
          <multiselect v-model="slotProps.data.value" :options="slotProps.data.options" placeholder="Select color"
            class="project-select multiselect-timeoff-color multiselect-color color-select" label="name"
            :showLabels="false" required @input="(value) => handleSlotFieldChange('color', value, slotProps)">
            <template slot="singleLabel" slot-scope="{ option }">
              <div class="multiselect-color__selected-box" :style="{ 'background-color': option.hex }">
              </div>
              <span> Color {{ getColorIndex(option.hex) + 1 }} </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <div class="multiselect-color__box" :style="{ 'background-color': props.option.hex }"></div>
                {{ props.option.name }}
              </div>
            </template>
          </multiselect>
        </b-form-group>
      </template>
      <template v-slot:icon="slotProps">
        <b-form-group>
          <label class="form__label input-label">{{ slotProps.data.label }}</label>
          <multiselect v-model="slotProps.data.value" :options="slotProps.data.options" placeholder="Select icon"
            :class="{
              'project-select': true,
              'multiselect-timeoff-color': true,
              'icon-multiselect': true,
              'multiselect-color': !!slotProps.data.value && !iconSelectOpen,
              'multiselect__single-line': true
            }" @open="iconSelectOpen = true" @close="iconSelectOpen = false"
            @input="(value) => handleSlotFieldChange('icon', value, slotProps)" label="name" :showLabels="false"
            required>
            <template slot="singleLabel" slot-scope="{ option }">
              <div>
                <TimeOffIcon :name="option" variant="secondary" />
                <span class="ml-1">Icon {{ getIconIndex(option) + 1 }}</span>
              </div>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="icon">
                  <TimeOffIcon :name="props.option" variant="secondary" />
                </span>
              </div>
            </template>
          </multiselect>
        </b-form-group>
      </template>

      <template v-slot:loggingProject="slotProps">
        <b-form-group>
          <label class="form__label input-label">{{ slotProps.data.label }}</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'right', boundary: 'viewport' }"
            :title="slotProps.data.value ? slotProps.data.value[slotProps.data.selectLabel] : null">
            <multiselect v-model="slotProps.data.value" :options="slotProps.data.options"
              :track-by="slotProps.data.valueField" :label="slotProps.data.selectLabel" :showLabels="false"
              :class="{ 'multiselect__single-line': true }" allow-empty @select="changeProject"
              @input="(value) => handleSlotFieldChange('loggingProject', value, slotProps)" />
          </div>
        </b-form-group>
      </template>
      <template v-slot:loggingTask="slotProps">
        <b-form-group>
          <label class="form__label input-label">{{ slotProps.data.label }}</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'right', boundary: 'viewport' }"
            :title="slotProps.data.value ? slotProps.data.value[slotProps.data.selectLabel] : null">
            <multiselect v-model="slotProps.data.value" :options="slotProps.data.options"
              :track-by="slotProps.data.valueField" :label="slotProps.data.selectLabel" :showLabels="false"
              :class="{ 'multiselect__single-line': true }"
              :placeholder="isFetchingTasks ? 'Loading...' : 'Select task'" :disabled="isFetchingTasks" allow-empty
              @input="(value) => handleSlotFieldChange('loggingTask', value, slotProps)" />
          </div>
        </b-form-group>
      </template>
      <template v-slot:isUnlimited="slotProps">
        <b-form-group class="btn-icon button-checkbox_sm w-100 ">
          <b-form-checkbox size="sm" switch v-model="slotProps.data.value" class="switch"
            @change="(value) => handleSlotFieldChange('isUnlimited', value, slotProps)">{{ slotProps.data.label
            }}</b-form-checkbox>
        </b-form-group>
      </template>
      <template v-slot:isPayable="slotProps">
        <b-form-group class="btn-icon button-checkbox_sm w-100 ">
          <b-form-checkbox size="sm" switch v-model="slotProps.data.value" class="switch"
            @change="(value) => handleSlotFieldChange('isPayable', value, slotProps)">{{ slotProps.data.label
            }}</b-form-checkbox>
        </b-form-group>
      </template>
      <template v-slot:accrueProportionallyByWorkingDays="slotProps">
        <b-form-group class="btn-icon button-checkbox_sm w-100 ">
          <b-form-checkbox size="sm" switch v-model="slotProps.data.value" class="switch"
            @change="(value) => handleSlotFieldChange('accrueProportionallyByWorkingDays', value, slotProps)">{{
              slotProps.data.label }}
            <div v-if="slotProps.data.value && slotProps.fields.maxDayPerYear.value > 0">
              {{ workingDaysInYear }} / {{ slotProps.fields.maxDayPerYear.value }} = {{ Number.parseFloat(
                workingDaysInYear / slotProps.fields.maxDayPerYear.value).toFixed(2) }} working days accrue 1 time off
            </div>
            <div v-else>
              (Accrue {{ slotProps.fields.maxDayPerYear.value }} time offs at the start of the year)
            </div>
          </b-form-checkbox>

        </b-form-group>
      </template>
      <template v-slot:considerAsWorkingDay="slotProps">
        <b-form-group class="btn-icon button-checkbox_sm w-100 ">
          <b-form-checkbox size="sm" switch v-model="slotProps.data.value" class="switch"
            @change="(value) => handleSlotFieldChange('considerAsWorkingDay', value, slotProps)"
            title="If enabled, other proportionally accrued leave types (e.g., paid vacation or sick leave) will continue to accrue while this time off is taken. If disabled, accruals will pause for the duration of this time off"
            v-b-tooltip.html="{ variant: 'base', placement: 'top', placement: 'top', boundary: 'viewport' }">{{
              slotProps.data.label }}
          </b-form-checkbox>
        </b-form-group>
      </template>
         <template v-slot:isDeactivated="slotProps">
        <b-form-group class="btn-icon button-checkbox_sm w-100">
          <b-form-checkbox size="sm" switch v-model="slotProps.data.value" class="switch" title="Deactivated time off types stay available in reports and past records, but users won’t be able to select or book them anymore."
            v-b-tooltip.html="{ variant: 'base', placement: 'top', boundary: 'viewport' }"
            @change="(value) => handleSlotFieldChange('isDeactivated', value, slotProps)">{{ slotProps.data.label
            }}</b-form-checkbox>
        </b-form-group>
      </template>
    </FormGenerator>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { TimeOffsSettingsIconsMap } from '@/constants/TimeOffsSettingsIconsList'
import FormGenerator from '@components/FormGenerator'
import Preloader from '@components/Preloader'
import TimeOffIcon from '@components/TimeOffIcon.vue'

export default {
  name: 'TimeOffsSettingsForm',
  components: { FormGenerator, Preloader, TimeOffIcon },
  data () {
    return {
      form: {
        name: {
          type: 'input',
          name: 'name',
          label: 'Name',
          placeholder: 'Enter time offs settings name',
          valueField: 'id',
          selectLabel: 'name',
          value: null,
          validation: [{ name: 'required' }],
          class: 'col-6'
        },
        approverRoles: {
          type: 'multiselect',
          name: 'approver_roles',
          label: 'Approver roles',
          placeholder: 'Approver roles',
          valueField: 'id',
          selectLabel: 'display_name',
          options: [],
          value: [],
          class: 'col-6'
        },
        isPayable: {
          type: 'slot',
          slotName: 'isPayable',
          name: 'is_payable',
          label: 'Payable',
          value: false,
          class: 'col-6'
        },
        isUnlimited: {
          type: 'slot',
          slotName: 'isUnlimited',
          name: 'is_unlimited',
          label: 'Unlimited days per year',
          value: false,
          class: 'col-6'
        },
        maxDayPerYear: {
          type: 'input',
          typeInput: 'number',
          name: 'max_day_per_year',
          label: 'Max days per year',
          placeholder: 'Max days per year',
          value: null,
          hidden: false,
          class: 'col-6',
          skipValidation: (fields) => fields.isUnlimited.value,
          validation: [
            { name: 'requiredIf', params: (fields) => !fields.isUnlimited.value },
            { name: 'maxValue', params: { max: 366 } }
          ]
        },
        maxDaysCanMoveToNextYear: {
          type: 'input',
          typeInput: 'number',
          name: 'max_days_can_move_to_next_year',
          label: 'Max carry-over days',
          placeholder: 'Max carry-over days',
          value: null,
          hidden: false,
          class: 'col-6',
          tooltip: 'Maximum number of unused days that can be carried over to the next year.',
          skipValidation: (fields) => fields.isUnlimited.value,
          validation: [
            { name: 'requiredIf', params: (fields) => !fields.isUnlimited.value },
            { name: 'maxValue', params: { max: (fields) => fields.maxDayPerYear.value || 999999 } }
          ]
        },
        accrueProportionallyByWorkingDays: {
          type: 'slot',
          slotName: 'accrueProportionallyByWorkingDays',
          name: 'accrue_proportionally_by_working_days',
          label: 'Accrue proportionally by working days',
          placeholder: 'Accrue proportionally by working days',
          value: false,
          hidden: false,
          class: 'col-12 pb-4'
        },
        considerAsWorkingDay: {
          type: 'slot',
          slotName: 'considerAsWorkingDay',
          name: 'consider_as_working_day',
          label: 'Accrue other leave during this time off',
          placeholder: 'Accrue other leave during this time off',
          value: false,
          hidden: false,
          class: 'col-12 pb-4'
        },
        isDeactivated: {
          type: 'slot',
          slotName: 'isDeactivated',
          name: 'isDeactivated',
          label: 'Account status',
          value: true,
          class: 'col-6',
          placeholder: 'test'
        },
        timeOffRules: {
          type: 'slot',
          slotName: 'timeOffRules',
          name: 'time_off_rules',
          label: 'Time off rules',
          placeholder: 'Time off rules',
          value: [],
          class: 'col-12 border-b'
        },
        color: {
          type: 'slot',
          slotName: 'color',
          name: 'color',
          label: 'Color',
          options: [
            { name: '', hex: '#F24949' },
            { name: '', hex: '#D81B60' },
            { name: '', hex: '#AB47BC' },
            { name: '', hex: '#477DD0' },
            { name: '', hex: '#0097A7' },
            { name: '', hex: '#43A047' },
            { name: '', hex: '#EF6C00' },
            { name: '', hex: '#8D6E63' },
            { name: '', hex: '#FF8F00' },
            { name: '', hex: '#9CCC65' },
            { name: '', hex: '#DD7E6B' },
            { name: '', hex: '#C27BA0' },
            { name: '', hex: '#8E7CC3' },
            { name: '', hex: '#6FA8DC' },
            { name: '', hex: '#134F5C' },
            { name: '', hex: '#93C47D' },
            { name: '', hex: '#CC4125' },
            { name: '', hex: '#85200C' },
            { name: '', hex: '#A64D79' },
            { name: '', hex: '#38761D' }
          ],
          value: { name: '', hex: '#F24949' },
          validation: [
            { name: 'required' }
          ],
          class: 'col-6 mt-3'
        },
        icon: {
          type: 'slot',
          slotName: 'icon',
          name: 'icon',
          label: 'Icon',
          options: Object.keys(TimeOffsSettingsIconsMap),
          value: '',
          validation: [
            { name: 'required' }
          ],
          class: 'col-6 mt-3'
        },
        loggingProject: {
          type: 'slot',
          slotName: 'loggingProject',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-6'
        },
        loggingTask: {
          type: 'slot',
          slotName: 'loggingTask',
          label: 'Task',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-6'
        }
      },
      limitTypes: ['from', 'to'],
      isFetchingTasks: false,
      workingDaysInYear: 260,
      iconSelectOpen: false
    }
  },
  computed: {
    ...mapGetters({
      getProjects: 'projects/getProjects',
      getTasks: 'tasks/getTasks'
    })
  },
  props: {
    dataProps: {
      type: Object,
      default: () => { }
    },
    isLoading: {
      type: Boolean,
      default: () => false
    }
  },
  mounted () {
    this.setInitialData()
  },
  async created () {
    this.form.approverRoles.options = this._allUserRoles
    await this.loadWorkingDaysCountForYear()
  },
  methods: {
    async setInitialData () {
      if (!this.getProjects.length) {
        await this.$api.projects.getProjects()
      }
      this.form.name.value = this.dataProps.name ? this.dataProps.name : null
      this.form.isPayable.value = this.dataProps.is_payable ? this.dataProps.is_payable : false
      this.form.isUnlimited.value = this.dataProps.is_unlimited ? this.dataProps.is_unlimited : false
      const administratorRole = this._allUserRoles.find(item => item.name === 'administrator')
      administratorRole.$isDisabled = true
      this.form.approverRoles.value = this.dataProps.approver_roles ? this.dataProps.approver_roles : [administratorRole]
      this.form.maxDayPerYear.value = this.dataProps.max_day_per_year
      this.form.timeOffRules.value = this.dataProps.time_off_rules ? this.dataProps.time_off_rules : []
      this.form.maxDaysCanMoveToNextYear.value = this.dataProps.max_days_can_move_to_next_year
      this.form.isDeactivated.value = !this.dataProps.is_deactivated
      this.form.accrueProportionallyByWorkingDays.value = this.dataProps.accrue_proportionally_by_working_days ? this.dataProps.accrue_proportionally_by_working_days : false
      this.form.considerAsWorkingDay.value = this.dataProps.consider_as_working_day ? this.dataProps.consider_as_working_day : false
      this.workingDaysInYear = this.dataProps.working_days_in_year ? this.dataProps.working_days_in_year : this.workingDaysInYear
      if (this.dataProps.color) {
        let selectedColor = this.form.color.options.find(item => item.hex === this.dataProps.color)
        if (!selectedColor) {
          selectedColor = { name: 'Custom', hex: this.dataProps.color }
          this.form.color.options.push(selectedColor)
        }
        this.form.color.value = selectedColor
      }

      if (this.dataProps.icon) {
        let selectedIcon = this.form.icon.options.find(item => item === this.dataProps.icon)
        if (selectedIcon) {
          this.form.icon.value = selectedIcon
        } else {
          this.form.icon.value = 'vacation'
        }
      }

      this.form.loggingProject.options = this.getProjects
      if (this.dataProps.logging_task) {
        if (this.dataProps.logging_task.project) {
          await this.changeProject(this.dataProps.logging_task.project)
        }
        this.form.loggingTask.value = this.dataProps.logging_task
      }

      // Set field visibility based on isUnlimited value
      this.form.maxDayPerYear.hidden =
        this.form.maxDaysCanMoveToNextYear.hidden =
        this.form.accrueProportionallyByWorkingDays.hidden = this.form.isUnlimited.value
    },
    onSubmit ({ name, isPayable, isUnlimited, approverRoles, maxDayPerYear, timeOffRules, maxDaysCanMoveToNextYear, accrueProportionallyByWorkingDays, considerAsWorkingDay, color, icon, loggingTask, isDeactivated }) {
      const approverRolesIds = approverRoles.value.map(item => item.id)

      this.$emit('submit', {
        name: name.value,
        is_payable: isPayable.value,
        is_unlimited: isUnlimited.value,
        approver_roles: approverRolesIds,
        time_off_rules: timeOffRules.value,
        accrue_proportionally_by_working_days: accrueProportionallyByWorkingDays.value,
        consider_as_working_day: considerAsWorkingDay.value,
        color: color.value.hex,
        icon: icon.value,
        is_deactivated: !isDeactivated.value,
        logging_task_id: loggingTask.value ? loggingTask.value.id : null,
        ...(!isUnlimited.value && {
          max_day_per_year: maxDayPerYear.value,
          max_days_can_move_to_next_year: maxDaysCanMoveToNextYear.value
        })
      })
    },
    updateItem (key, itemData) {
      if (key === 'isUnlimited') {
        this.form.maxDayPerYear.hidden =
          this.form.maxDaysCanMoveToNextYear.hidden =
          this.form.accrueProportionallyByWorkingDays.hidden = itemData.value
      }
      this.form[key].value = itemData.value
    },
    addTimeOffRule (timeOffRules) {
      timeOffRules.push({
        'limitType': 'from',
        'limit': 0,
        'buffer': 0
      })
    },
    removeTimeOffRule (timeOffRules, index) {
      timeOffRules.splice(index, 1)
    },
    getColorIndex (hex) {
      return this.form.color.options.findIndex(color => color.hex === hex)
    },
    getIconIndex (value) {
      return this.form.icon.options.indexOf(value)
    },
    async changeProject (project) {
      this.form.loggingProject.value = project
      this.form.loggingTask.options = []
      this.form.loggingTask.value = null
      this.isFetchingTasks = true
      await this.$api.tasks.getTasks({ project_ids: [project.id], labels_only: true })
      this.isFetchingTasks = false
      this.form.loggingTask.options = this.getTasks
      if (this.form.loggingTask.options.length) {
        this.form.loggingTask.value = this.form.loggingTask.options[0]
      }
    },
    async loadWorkingDaysCountForYear () {
      try {
        const response = await this.axios.get('/api/working-days-count-for-year', {
          headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') }
        })
        if (response.data && response.data.working_days_count) {
          this.workingDaysInYear = response.data.working_days_count
        }
      } catch (error) {
        console.error('Failed to load working days count:', error)
      }
    },
    handleSlotFieldChange (fieldKey, value, slotProps) {
      if (slotProps.updateField) {
        slotProps.updateField(value)
      } else {
        // Fallback to manual update
        this.updateItem(fieldKey, { ...slotProps.data, value: value })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.time-off-rules-label {
  font-size: 18px;
  font-weight: 500;
}

.custom-select-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.time-off-rules {
  margin-left: -10px;

  fieldset {
    flex: 0 0 30%;

    .custom-select {
      color: #3d5170;
      background: #f3f5f7;
      border: 1px solid #cacae1;
      border-radius: 4px;
      min-height: $input-height;
      padding: 4px 12px;
      font-weight: 500;
    }
  }
}

.delete-btn {
  height: $input-height;
  min-width: 50px;
  display: flex;
  margin-left: 15px;
  align-items: center;
  justify-content: center;
}

.add-button {
  background-color: $notifySuccessColor;
  min-width: 244px;
  border: 0;
  font-size: 14px;
  font-weight: 700;
  font-family: $font-family-second;
  text-transform: uppercase;

  &:focus,
  &:hover,
  &:active {
    background-color: $notifySuccessColor;
    box-shadow: none;
  }
}
</style>
